/**
 * Excess Service — API communication layer for transaction excesses.
 *
 * Related issues:
 * - https://github.com/MangoTec/mango-api/issues/77 [CDC-014] Servicio de excedentes en mango-app
 * - https://github.com/MangoTec/mango-api/issues/80 [CDC-017] Funcionalidad Marcar como devuelto
 *
 * Spec ref: §6.6.4, §6.6.5
 */
import SentryService from "./sentryService";

const ExcessService = {
  // Sanctum-authenticated endpoints \u2014 supplier_id is derived server-side from the auth user
  url: `${process.env.VUE_APP_API_URL}supplier/transaction-excesses`,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },

  /**
   * Fetch paginated list of transaction excesses for the authenticated supplier.
   * @param {number|string} supplierId - Unused (kept for call-site compatibility)
   * @param {Object} filters - Optional filters: { status, date_from, date_to, page }
   * @returns {Promise<Response|false>}
   */
  getExcesses: async (supplierId, filters = {}) => {
    try {
      const headers = { ...ExcessService.headers };
      headers["Authorization"] =
        "Bearer " + localStorage.getItem("token");

      // supplier_id is not sent — server derives it from the Sanctum token
      const params = new URLSearchParams();

      // Append optional filters when present
      if (filters.status) params.append("status", filters.status);
      if (filters.date_from) params.append("date_from", filters.date_from);
      if (filters.date_to) params.append("date_to", filters.date_to);
      if (filters.page) params.append("page", filters.page);
      if (filters.per_page) params.append("per_page", filters.per_page);

      const query = params.toString() ? `?${params}` : "";
      const response = await fetch(
        `${ExcessService.url}${query}`,
        { headers }
      );

      return response;
    } catch (e) {
      SentryService.captureError(e, {
        action: "excess.getExcesses",
        supplierId,
        filters: JSON.stringify(filters),
      });
      return false;
    }
  },

  /**
   * Fetch aggregated summary counts and amounts for the authenticated supplier.
   * @param {number|string} supplierId - Unused (kept for call-site compatibility)
   * @returns {Promise<Response|false>}
   */
  getSummary: async (supplierId) => {
    try {
      const headers = { ...ExcessService.headers };
      headers["Authorization"] =
        "Bearer " + localStorage.getItem("token");

      // supplier_id is not sent — server derives it from the Sanctum token
      const response = await fetch(
        `${ExcessService.url}/summary`,
        { headers }
      );

      return response;
    } catch (e) {
      SentryService.captureError(e, {
        action: "excess.getSummary",
        supplierId,
      });
      return false;
    }
  },

  /**
   * Mark a transaction excess as returned.
   * @param {number|string} id - Transaction excess ID
   * @param {Object} data - { return_reference: string, notes?: string }
   * @returns {Promise<Response|false>}
   */
  markAsReturned: async (id, data) => {
    try {
      const headers = { ...ExcessService.headers };
      headers["Authorization"] =
        "Bearer " + localStorage.getItem("token");

      const response = await fetch(
        `${ExcessService.url}/${id}/return`,
        {
          method: "POST",
          body: JSON.stringify(data),
          headers,
        }
      );

      return response;
    } catch (e) {
      SentryService.captureError(e, {
        action: "excess.markAsReturned",
        excessId: id,
        data: JSON.stringify(data),
      });
      return false;
    }
  },
};

export default ExcessService;
